import { SkyDialog, SkyMessageBar, SkyTextarea } from "@skypointcloud/skypoint-ui";
import { useState } from "react";
import type { InstanceAdminView } from "@shared/types/instancesAdmin";
import { useMcpEnabled } from "@/hooks/use-mcp";
import { ErrorState } from "./ScreenState";

/**
 * Enable a disabled instance (round 9, B-15).
 *
 * The reason is required: it is written to the audit row beside the actor, and a reviewer asks why
 * a customer's environment came back. Enabling serves the instance's screens and the runner picks
 * it up on its next pass; the schedules that Disable switched off stay off until someone enables
 * each one, and Connected AI apps ended by the Disable must be approved again.
 */
export function EnableInstanceDialog({
  open,
  onOpenChange,
  instance,
  onConfirm,
  isSaving,
  error,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  instance: InstanceAdminView | null;
  onConfirm: (reason: string) => void;
  isSaving: boolean;
  error: unknown;
}) {
  // Empty on every opening: the caller remounts it with a key.
  const [reason, setReason] = useState("");
  const mcpEnabled = useMcpEnabled();
  const [asked, setAsked] = useState(false);

  if (!instance) return null;
  const trimmed = reason.trim();

  return (
    <SkyDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Enable ${instance.name}`}
      description="Its screens and imports come back. Nothing that happened while it was disabled is replayed."
      size="medium"
      testId="instance-enable-dialog"
      // Closes only when the call succeeds, so a refusal leaves the typing where it is.
      onBeforeClose={(reasonToClose) => reasonToClose !== "primaryAction"}
      primaryAction={{
        label: "Enable instance",
        onClick: () => {
          setAsked(true);
          if (trimmed === "") return;
          onConfirm(trimmed);
        },
        loading: isSaving,
      }}
      secondaryAction={{ label: "Keep disabled", onClick: () => onOpenChange(false) }}
    >
      <div className="flex flex-col gap-4">
        {error ? <ErrorState title="That change was not saved" error={error} /> : null}

        <SkyMessageBar
          intent="warning"
          layout="multiline"
          title="Schedules stay off"
          // #1812: AI apps are named only while they exist on this deployment.
          message={`Disabling the instance switched off every schedule in it. They stay off until someone enables each one on Schedules.${mcpEnabled ? " Connected AI apps must be approved again." : ""}`}
        />

        <SkyTextarea
          label="Reason"
          required
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          hint="Recorded on the audit event beside your name."
          validationState={asked && trimmed === "" ? "error" : "none"}
          validationMessage={
            asked && trimmed === "" ? "Say why the instance is coming back." : undefined
          }
          maxLength={500}
          rows={3}
          testId="instance-enable-reason"
        />
      </div>
    </SkyDialog>
  );
}
